import React from 'react';
import {
  HttpError,
  IResourceComponentsProps,
  usePermissions,
  useTranslate,
} from '@pankod/refine-core';
import {
  Create,
  Box,
  FormControl,
  FormHelperText,
  Grid,
  Stack,
  TextField,
  Typography,
  Select,
  MenuItem,
  Backdrop,
  CircularProgress,
  IconButton,
  InputLabel,
} from '@pankod/refine-mui';
import { Refresh } from '@mui/icons-material';
import { useStepsForm } from '@pankod/refine-react-hook-form';
import { IOfferCodes } from 'interfaces';
import _ from 'lodash';
import { randomCode } from 'utils/utils';
import { fbAuth, firestoreDatabase } from 'lib/firebaseService';
import { OfferExpiryType, PeriodOptions } from 'config/enum';
import { getOfferLimit } from 'services/adminUserService';
import { countOwnedOffers } from 'services/offerService';
import { useRole } from 'hooks/auth/permissions/useRole';
import { useOfferLeft } from 'hooks/offers/useOfferLeft';

type IOfferCreateForm = IOfferCodes & { period_key: string };

export const OfferCodeCreate: React.FC<
  IResourceComponentsProps
> = () => {
  const t = useTranslate();
  const { offerLeftText } = useOfferLeft();
  const { isAdmin } = useRole();

  const {
    refineCore: { onFinish, formLoading },
    register,
    setValue,
    getValues,
    setError,
    formState: { errors },
  } = useStepsForm<IOfferCodes, HttpError, IOfferCreateForm>({
    stepsProps: {
      isBackValidate: false,
    },
    warnWhenUnsavedChanges: true,
    defaultValues: {
      code: randomCode(8),
      limit: 1,
    },
  });

  const handleSubmit = async () => {
    const code = getValues('code');
    const isCodeExisted = await checkCodeExisted(code);
    if (isCodeExisted) {
      return;
    }

    let currentUserId = '',
      currentUserRole = '';
    try {
      const { uid, offerLimit, role } = await getOfferLimit();
      if (offerLimit > 0) {
        const offerCount = await countOwnedOffers(uid);
        if (offerCount >= offerLimit) {
          setError('limit', {
            type: 'manual',
            message:
              'You have reached the maximum number of codes you can create',
          });
          return;
        }
      }

      currentUserId = uid;
      currentUserRole = role;
    } catch (error) {
      console.error('Error fetching offer limit:', error);
      return;
    }

    const now = new Date();
    const { period_key: selectedPeriod, ...submittedData } =
      getValues();
    const currentUserEmail = fbAuth.currentUser?.email || '';

    const limit = isAdmin ? +getValues('limit') : 1;
    const data = {
      ...submittedData,
      limit,
      period_type: PeriodOptions[selectedPeriod].period_type,
      period: PeriodOptions[selectedPeriod].period,
      type: OfferExpiryType.ByPeriod,
      use: 0,
      created_by_uid: currentUserId,
      created_by_email: currentUserEmail,
      created_by_role: currentUserRole,
      created_at: now,
      updated_at: now,
    };

    onFinish(data as any);
  };
  const checkCodeExisted = async (code: string) => {
    const res = await firestoreDatabase.getList({
      resource: 'Offers',
      filters: [{ field: 'code', operator: 'eq', value: code }],
    });
    if (res.data.length === 0) {
      return false;
    }

    setError('code', {
      type: 'manual',
      message: 'Code is existed',
    });
    return true;
  };

  const renderForm = () => {
    return (
      <>
        <Grid
          container
          sx={{
            marginX: { xs: '0px' },
          }}
        >
          <Grid item xs={12}>
            <Grid container>
              <Grid item paddingX={4} xs={12}>
                <Stack gap="24px">
                  <FormControl>
                    <TextField
                      {...register('name', {
                        required: t('errors.required.field', {
                          field: 'name',
                        }),
                        maxLength: {
                          value: 100,
                          message: t('errors.required.max', {
                            max: 100,
                          }),
                        },
                      })}
                      size="medium"
                      variant="outlined"
                      required
                      label="Name (required)"
                    />
                    {errors.name && (
                      <FormHelperText error>
                        {errors.name.message}
                      </FormHelperText>
                    )}
                  </FormControl>

                  <FormControl>
                    <Stack direction="row">
                      <TextField
                        {...register('code', {
                          required: t('errors.required.field', {
                            field: 'code',
                          }),
                        })}
                        size="medium"
                        variant="filled"
                        required
                        label={`Code (Length: ${getValues('code')?.length || 0})`}
                        sx={{ flex: 1 }}
                      />
                      {/* Button Generate  */}
                      <IconButton
                        onClick={() => {
                          setError('code', {
                            type: 'manual',
                            message: '',
                          });
                          setValue('code', randomCode(8));
                        }}
                      >
                        <Refresh />
                      </IconButton>
                    </Stack>
                    {errors.code && (
                      <FormHelperText error>
                        {errors.code.message}
                      </FormHelperText>
                    )}
                  </FormControl>

                  {/* Period */}
                  <FormControl>
                    <InputLabel id="period_key">Period</InputLabel>

                    <Select
                      labelId="period_key"
                      {...register('period_key', {
                        required: t('errors.required.field', {
                          field: 'period_key',
                        }),
                      })}
                      size="medium"
                      variant="filled"
                      label="Period"
                      required
                    >
                      {Object.values(PeriodOptions).map((item) => (
                        <MenuItem key={item.key} value={item.key}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>

                    {errors.period_key && (
                      <FormHelperText error>
                        {errors.period_key.message}
                      </FormHelperText>
                    )}
                  </FormControl>

                  {isAdmin && (
                    <FormControl>
                      <TextField
                        {...register('limit', {
                          required: t('errors.required.field', {
                            field: 'limit',
                          }),
                        })}
                        size="medium"
                        variant="filled"
                        label="Limit"
                        required
                        type="number"
                      />
                      {errors.limit && (
                        <FormHelperText error>
                          {errors.limit.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <Create
      title={<Typography variant="h5">Create OfferCode{offerLeftText}</Typography>}
      isLoading={formLoading}
      saveButtonProps={{ onClick: handleSubmit }}
    >
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
        autoComplete="off"
      >
        <br />
        {renderForm()}
        <Backdrop
          sx={{
            color: '#fff',
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open={formLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Box>
    </Create>
  );
};
